import { BrowserModule } from "@angular/platform-browser";
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HttpClientModule, HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from "@angular/common/http";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader, provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { LoginComponent } from "./login/login.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { LoadingSpinnerComponent } from "./shared/loading-spinner/loading-spinner.component";
import { JwtInterceptor } from "./shared/_helpers/jwt.interceptor";
import { ErrorInterceptor } from "./shared/_helpers/error.interceptor";
import { SharedService } from "./shared/shared.service";
import { MaterialModule } from "./material.module";
import { ToolbarComponent } from "./toolbar/toolbar.component";
import { UpdateFlightDialogComponent } from "./update-flight/update-flight-dialog/update-flight-dialog.component";
import { PilotNamePipe } from "./shared/pipes/pilot-name.pipe";
import { UpdateFlightComponent } from "./update-flight/update-flight.component";
import { HelicopterPipe } from "./shared/pipes/helicopter.pipe";
import { FlightTypePipe } from "./shared/pipes/flight-type.pipe";
import { FlightOperationPipe } from "./shared/pipes/flight-operation.pipe";
import { UpdatePassengerDialogComponent } from "./passengers/update-passenger-dialog/update-passenger-dialog.component";
import { NationalityPipe } from "./shared/pipes/nationality.pipe";
import { PilotRolePipe } from "./shared/pipes/pilot-role.pipe";
import { ConfirmationDialogComponent } from "./shared/confirmation-dialog/confirmation-dialog.component";
import { PassengersComponent } from "./passengers/passengers.component";
import { FormGroupTitleComponent } from "./shared/form-group-title/form-group-title.component";
import { PicturePreviewDialogComponent } from "./shared/picture-preview-dialog/picture-preview-dialog.component";
import { UpdateFlightAssignedPassengersDialogComponent } from "./passengers/update-flight-assigned-passengers-dialog/update-flight-assigned-passengers-dialog.component";
import { AssignePassengersToFlightComponent } from "./passengers/assigne-passengers-to-flight/assigne-passengers-to-flight.component";
import { FlightStatusPipe } from "./shared/pipes/flight-status.pipe";
import { HtlComponent } from "./htl/htl.component";
import { HelicoptersComponent } from "./helicopters/helicopters.component";
import { UpdateHtlBlockComponent } from "./htl/htl-blocks/update-htl-block/update-htl-block.component";
import { HtlBlockStatusPipe } from "./shared/pipes/htl-block-status.pipe";
import { HoldItemListComponent } from "./hold-item-list/hold-item-list.component";
import { UpdateHoldItemListDialogComponent } from "./hold-item-list/update-hold-item-list-dialog/update-hold-item-list-dialog.component";
import { UpdateExpiringHoldItemListDialogComponent } from "./hold-item-list/update-expiring-hold-item-list-dialog/update-expiring-hold-item-list-dialog.component";
import { NextDueMaintenanceComponent } from "./next-due-maintenance/next-due-maintenance.component";
import { NextScheduledInspectionsDialogComponent } from "./htl/next-scheduled-inspections-dialog/next-scheduled-inspections-dialog.component";
import { UpdateNextDueMaintenanceDialogComponent } from "./next-due-maintenance/update-next-due-maintenance-dialog/update-next-due-maintenance-dialog.component";
import { PublicLayoutComponent } from "./public-layout/public-layout.component";
import { HtlPdfViewerComponent } from "./public-layout/htl-pdf-viewer/htl-pdf-viewer.component";
import { HoldItemListTableComponent } from "./hold-item-list/hold-item-list-table/hold-item-list-table.component";
import { NextDueMaintenanceTableComponent } from "./next-due-maintenance/next-due-maintenance-table/next-due-maintenance-table.component";
import { AirportAutocompleteSearchComponent } from "./shared/airport-autocomplete-search/airport-autocomplete-search.component";
import { FlightsComponent } from "./flights/flights.component";
import { FlightsTableComponent } from "./flights/flights-table/flights-table.component";
import { AppLayoutComponent } from "./app-layout/app-layout.component";
import { DefaultMatCalendarRangeStrategy, MatDatepickerModule, MatRangeDateSelectionModel } from "@angular/material/datepicker";
import { StatisticPilotHoursComponent } from "./statistic-pilot-hours/statistic-pilot-hours.component";
import { UpdateProfileComponent } from "./profile/update-profile/update-profile.component";
import { ExcelModule, ExcelService, GridModule, PDFModule } from "@progress/kendo-angular-grid";
import { ExcelExportModule } from "@progress/kendo-angular-excel-export";
import { ExportDataComponent } from "./export-data/export-data.component";
import { L10N_PREFIX, LocalizationService } from "@progress/kendo-angular-l10n";
import { ExportDataDialogueComponent } from "./export-data/export-data-dialogue/export-data-dialogue.component";
import { ExportDataPasswordDialogueComponent } from "./export-data/export-data-password-dialogue/export-data-password-dialogue.component";
import { HtlPasswordDialogComponent } from "./htl/htl-password-dialog/htl-password-dialog.component";
import { EditQtbComponent } from "./edit-qtb/edit-qtb.component";
import { EditQtbDialogComponent } from "./edit-qtb/edit-qtb-dialog/edit-qtb-dialog.component";
import { SpreadsheetModule } from "@progress/kendo-angular-spreadsheet";
import { AppConfigComponent } from "./app-config/app-config.component";
import { EditQtbCancelDialogComponent } from "./edit-qtb/edit-qtb-cancel-dialog/edit-qtb-cancel-dialog.component";
import { ExternalRedirectComponent } from "./external-redirect.component";
import { NgArrayPipesModule, NgBooleanPipesModule, NgObjectPipesModule } from "ngx-pipes";
import { CldrIntlService, IntlModule, IntlService } from "@progress/kendo-angular-intl";
import { PdfViewerModule } from "ng2-pdf-viewer";
import "@progress/kendo-angular-intl/locales/en/all";
import "@progress/kendo-angular-intl/locales/de/all";
import "@progress/kendo-angular-intl/locales/it/all";
import { AccessValuePipe } from "./shared/pipes/access-value.pipe";
import { ExportDataColumnsDialogueComponent } from "./export-data/export-data-columns-dialogue/export-data-columns-dialogue.component";
import { DailyChecksComponent } from "./daily-checks/daily-checks.component";
import { DailyChecksTableComponent } from "./daily-checks/daily-checks-table/daily-checks-table.component";
import { PDFExportModule } from "@progress/kendo-angular-pdf-export";
import { DateInputsModule } from "@progress/kendo-angular-dateinputs";
import { LabelModule } from "@progress/kendo-angular-label";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { HelicoptersUploadDocumentDialogComponent } from "./helicopters/helicopters-upload-document-dialog/helicopters-upload-document-dialog.component";
import { HelicoptersDeleteDocumentDialogComponent } from "./helicopters/helicopters-delete-document-dialog/helicopters-delete-document-dialog.component";
import { NextDueMaintenanceDeleteDialogComponent } from "./next-due-maintenance/next-due-maintenance-delete-dialog/next-due-maintenance-delete-dialog.component";
import { TestBannerComponent } from "./shared/test-banner/test-banner.component";
import { PopupModule } from "@progress/kendo-angular-popup";
import { SidenavComponent } from "./app-layout/sidenav/sidenav.component";
import { LayoutModule } from "@progress/kendo-angular-layout";
import { IconsModule } from "@progress/kendo-angular-icons";
import { FilterConfirmDialogComponent } from "./export-data/export-data-columns-dialogue/filter-confirm-dialog/filter-confirm-dialog.component";
import { AircraftActivityComponent } from "./aircraft-activity/aircraft-activity.component";
import { DatePipe } from "@angular/common";
import { PilotActivityComponent } from "./pilot-activity/pilot-activity.component";
import { ButtonComponent } from "@progress/kendo-angular-buttons";
import { NgxTurnstileFormsModule, NgxTurnstileModule } from "ngx-turnstile";
import { CreatePasswordComponent } from "./create-password/create-password.component";
import { AIPageComponent } from "./ai-page/ai-page.component";
import { AvioblueChatbotComponent, provideChatbot, identityInterceptor } from "@rrsolutionsit/atl-chatbot-lib";
import { provideMarkdown } from "ngx-markdown";
import { environment } from "../environments/environment";

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        DashboardComponent,
        LoadingSpinnerComponent,
        ToolbarComponent,
        UpdateFlightDialogComponent,
        PilotNamePipe,
        UpdateFlightComponent,
        HelicopterPipe,
        FlightTypePipe,
        FlightOperationPipe,
        AccessValuePipe,
        UpdatePassengerDialogComponent,
        NationalityPipe,
        PilotRolePipe,
        ConfirmationDialogComponent,
        PassengersComponent,
        FormGroupTitleComponent,
        PicturePreviewDialogComponent,
        UpdateFlightAssignedPassengersDialogComponent,
        AssignePassengersToFlightComponent,
        FlightStatusPipe,
        HtlComponent,
        HtlPasswordDialogComponent,
        EditQtbComponent,
        EditQtbDialogComponent,
        HelicoptersComponent,
        UpdateHtlBlockComponent,
        HtlBlockStatusPipe,
        HoldItemListComponent,
        UpdateHoldItemListDialogComponent,
        UpdateExpiringHoldItemListDialogComponent,
        NextDueMaintenanceComponent,
        NextScheduledInspectionsDialogComponent,
        UpdateNextDueMaintenanceDialogComponent,
        PublicLayoutComponent,
        HtlPdfViewerComponent,
        HoldItemListTableComponent,
        NextDueMaintenanceTableComponent,
        AirportAutocompleteSearchComponent,
        FlightsComponent,
        FlightsTableComponent,
        AppLayoutComponent,
        ExternalRedirectComponent,
        StatisticPilotHoursComponent,
        UpdateProfileComponent,
        ExportDataComponent,
        ExportDataDialogueComponent,
        ExportDataColumnsDialogueComponent,
        ExportDataPasswordDialogueComponent,
        AppConfigComponent,
        EditQtbCancelDialogComponent,
        DailyChecksComponent,
        DailyChecksTableComponent,
        HelicoptersUploadDocumentDialogComponent,
        HelicoptersDeleteDocumentDialogComponent,
        NextDueMaintenanceDeleteDialogComponent,
        SidenavComponent,
        FilterConfirmDialogComponent,
        AircraftActivityComponent,
        PilotActivityComponent,
        CreatePasswordComponent,
        AIPageComponent,
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateHttpLoader,
            },
        }),
        BrowserAnimationsModule,
        FlexLayoutModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        MatDatepickerModule,
        GridModule,
        ExcelModule,
        ExcelExportModule,
        SpreadsheetModule,
        PdfViewerModule,
        NgArrayPipesModule,
        NgBooleanPipesModule,
        IntlModule,
        NgObjectPipesModule,
        PDFModule,
        PDFExportModule,
        DateInputsModule,
        LabelModule,
        InputsModule,
        TestBannerComponent,
        PopupModule,
        LayoutModule,
        IconsModule,
        ButtonComponent,
        NgxTurnstileModule,
        NgxTurnstileFormsModule,
        AvioblueChatbotComponent,
    ],
    providers: [
        { provide: IntlService, useClass: CldrIntlService },
        { provide: LOCALE_ID, useValue: "en" },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        SharedService,
        ExcelService,
        LocalizationService,
        { provide: L10N_PREFIX, useValue: "" },
        DefaultMatCalendarRangeStrategy,
        MatRangeDateSelectionModel,
        DatePipe,
        provideChatbot({
            apiUrl: environment.aiApiUrl,
            surface: "embedded",
            primaryColor: "var(--mat-primary-color)",
            fontSize: "var(--app-font-size)",
            allowedHostOrigins: [window.location.origin],
            allowInsecureTransport: environment.local,
            domains: {
                camo: {
                    agent: "camo_agent",
                    identity: "bearer",
                    allowsSessionManagement: true,
                    showsAgentPicker: false,
                },
            },
        }),
        provideMarkdown(),
        // `HttpClientModule` only wires up the classic HTTP_INTERCEPTORS DI token, while the
        // chatbot's own identity interceptor is a functional one and needs this separate provider.
        provideHttpClient(withInterceptors([identityInterceptor])),
        provideTranslateHttpLoader({ prefix: "./assets/i18n/", suffix: ".json" }),
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
